'use client';

import clsx from 'clsx';

import { useEffect, useState } from 'react';

import { KalturaPlayerAPI } from '@carbon/ibmdotcom-services';
import { Close } from '@carbon/icons-react';
import { Column, Grid, IconButton, Loading } from '@carbon/react';

import { VideoPlayer } from '@/components/VideoPlayer';

import styles from './video-player-modal.module.scss';

export interface Props {
  videoId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function VideoPlayerModal({ videoId, isOpen, onClose }: Props) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(isOpen);
  const [description, setDescription] = useState<string>();
  const [title, setTitle] = useState<string>();
  const [duration, setDuration] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  const wrapperClassName = clsx(styles['video-player-modal'], {
    [styles['video-player-modal--open']]: isVideoPlaying,
  });

  function closeModal() {
    setIsVideoPlaying(false);
    onClose();
  }

  useEffect(() => {
    setLoading(true);
    KalturaPlayerAPI.api(videoId).then((data: any) => {
      setDescription(data.description);
      setTitle(data.name);
      setDuration(formatDuration(data.duration));
      setLoading(false);
    });
  }, [videoId]);

  return (
    <div
      className={wrapperClassName}
      onClick={closeModal}
      data-autotrackregion-namespace={`Video modal for ${videoId}`}
    >
      <div className={styles['video-player-modal__container']} onClick={(e) => e.stopPropagation()}>
        <div className="absolute right-0 top-0">
          <IconButton autoAlign kind="ghost" label="Close" onClick={closeModal}>
            <Close size={20} />
          </IconButton>
        </div>
        {loading && (
          <div className="w-full h-full flex items-center justify-center aspect-video">
            <Loading active description="Loading video player" withOverlay={false} />
          </div>
        )}
        {!loading && (
          <Grid className={styles['video-player-modal__container__content']}>
            <Column sm={4} md={8} lg={12}>
              <VideoPlayer
                videoId={videoId}
                thumbnailAlt={description || ''}
                play={isVideoPlaying}
                className={styles['video-player-modal__container__video-player']}
                showControls={true}
              />
            </Column>
            <Column sm={4} md={8} lg={4}>
              <h5 className={styles['video-player-modal__container__content__title']}>
                {title} {duration}
              </h5>
              <p dangerouslySetInnerHTML={{ __html: description || '' }} />
            </Column>
          </Grid>
        )}
      </div>
    </div>
  );
}

function formatDuration(durationInSeconds: number): string {
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = Math.floor(durationInSeconds % 60);

  // Pad single-digit seconds with a leading zero
  const formattedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;

  // Return the formatted time
  return `(${minutes}:${formattedSeconds})`;
}
